import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/entities/Session';
import { useUserStore } from '@/entities/User';
import { permissionGuard } from './permission.guard';
import { invalidItemNameGuard } from './invalidItemName.guard';

const MEMBER_ONLY_ROOT_NAMES = new Set(['FormBuilder', 'EditarFormulario', 'EditarModelo']);

function isMemberOnlyArea(to: RouteLocationNormalized): boolean {
  const root = to.matched[0];
  if (!root) return false;
  return root.path === '/' || MEMBER_ONLY_ROOT_NAMES.has(String(root.name));
}

export function navigationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login' });
    return;
  }

  if (to.name === 'Login' && authStore.isAuthenticated) {
    next({ name: 'Home' });
    return;
  }

  const userStore = useUserStore();
  if (
    authStore.isAuthenticated &&
    userStore.isSuperuser &&
    !userStore.isMemberOfSelectedBranch &&
    isMemberOnlyArea(to)
  ) {
    next({ name: 'Admin' });
    return;
  }

  if (to.name === 'FilteredList') {
    const afterItemName = ((...args: unknown[]) => {
      if (args.length === 0) {
        permissionGuard(to, from, next);
      } else {
        (next as (...forwarded: unknown[]) => void)(...args);
      }
    }) as NavigationGuardNext;
    invalidItemNameGuard(to, from, afterItemName);
    return;
  }

  permissionGuard(to, from, next);
}
